// Shared presentation formatters for the web app.
//
// THIS MODULE IS THE ONE HOME OF THE MONEY FORMAT for `apps/web` (SEP-243
// decision D10). Every monetary value that a screen shows starts with its
// currency symbol and carries comma separators, in the en-GB locale. One
// million pounds reads `£1,000,000`, never `1000000`. A page must not build a
// money string from its own template literal, and it must not call
// `toLocaleString` on a money value.
//
// The server keeps a DOM-free twin at `apps/server/src/lib/format-currency.ts`,
// because a PDF template, the mandate docx builder and the MCP tools cannot
// import a React module. The two agree, and a change to one belongs in both.
//
// FOUR MONEY SHAPES, and no more:
//
//   - `formatCurrencyGBP`     `£1,234,567`     whole pounds, the default;
//   - `formatCurrencyGBP2dp`  `£1,234.50`      pence, for a price or a fee;
//   - `formatSignedCurrency`  `-$1,234.50`     pence, in the row's own currency;
//   - `formatCompactGBP`      `£1.5M`          an abbreviation, for a roll-up
//                                              tile only, because it drops the
//                                              pence.
//
// Each shape is sign-aware: a negative amount reads `-£5,000`, and the minus
// sign comes before the symbol.
//
// WHAT THIS MODULE DOES NOT FORMAT: a share count, a quantity, a percentage and
// a chart axis tick are not money. Keep them out.

const CURRENCY_SYMBOLS: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };

/**
 * The one money renderer. It rounds to the decimal count first, so a value that
 * rounds to zero never keeps a stray minus sign.
 */
function renderAmount(value: number, currency: string, decimals: 0 | 2): string {
    const factor = decimals === 0 ? 1 : 100;
    const rounded = Math.round(value * factor) / factor;
    const sign = rounded < 0 ? '-' : '';
    const body = Math.abs(rounded).toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    const symbol = CURRENCY_SYMBOLS[currency];
    return symbol ? `${sign}${symbol}${body}` : `${sign}${body} ${currency}`;
}

/** Full GBP to whole pounds — `£1,234,567` / `-£5,000`. */
export function formatCurrencyGBP(value: number): string {
    return renderAmount(value, 'GBP', 0);
}

/**
 * Full GBP with pence — `£1,234,567.89` / `-£5,000.00`.
 *
 * Use it for a price, a fee and any figure whose pence the reader needs. The
 * KPI Reporting display standard (spec 12) asks for this shape.
 */
export function formatCurrencyGBP2dp(value: number): string {
    return renderAmount(value, 'GBP', 2);
}

/**
 * Sign-aware amount with pence in the currency of the row — `-£1,234.50` /
 * `$99.00` / `12.00 CHF`. GBP, USD and EUR render their symbol; any other
 * currency takes a trailing ISO code, because a wrong symbol reads worse than
 * no symbol. Shared by the Ops and Desk overdrawn-cash surfaces (the Desk page
 * is a read-only mirror of the Ops one, so the two always move together).
 */
export function formatSignedCurrency(value: number, currency: string): string {
    return renderAmount(value, currency, 2);
}

/**
 * Compact GBP abbreviation — `£2.30B` / `£1.5M` / `£250K` / `£500`.
 * Sign-aware, so a fall in AUM reads `-£250K`. The abbreviation letter is UPPER
 * case, which is the convention of the firm.
 *
 * A ROLL-UP SHAPE ONLY. The band drops the pence, so a figure that a reader
 * reconciles or pays — a cash break, a fee, a price — takes
 * `formatCurrencyGBP2dp` or `formatSignedCurrency` instead.
 *
 * It rounds to the band FIRST and reads the sign afterwards, so an amount that
 * rounds to zero keeps no stray minus sign, and an amount that rounds up out of
 * its band moves to the band above it: 999,999 reads `£1.0M`, not `£1000K`.
 */
export function formatCompactGBP(value: number): string {
    const body = compactBody(Math.abs(value));
    const sign = value < 0 && body !== '0' ? '-' : '';
    return `${sign}£${body}`;
}

const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

/** The unsigned body of the compact shape, with the band promotion. */
function compactBody(abs: number): string {
    if (abs >= BILLION) return `${(abs / BILLION).toFixed(2)}B`;
    if (abs >= MILLION) {
        const millions = (abs / MILLION).toFixed(1);
        return Number(millions) >= THOUSAND ? `${(abs / BILLION).toFixed(2)}B` : `${millions}M`;
    }
    if (abs >= THOUSAND) {
        const thousands = (abs / THOUSAND).toFixed(0);
        return Number(thousands) >= THOUSAND ? `${(abs / MILLION).toFixed(1)}M` : `${thousands}K`;
    }
    return Math.round(abs).toLocaleString('en-GB');
}

/**
 * British short date — `02 Jun 2026` (en-GB, `dd MMM yyyy`). Passes a malformed
 * or unparseable input back through unchanged. Date-only strings (`YYYY-MM-DD`)
 * are anchored to UTC midnight so they don't drift a day in negative timezones.
 */
export function formatDateGB(iso: string): string {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00.000Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * British short date and time — `20 Aug 2026, 09:00` (en-GB). Use it when the
 * reader must tell two entries of one day apart, for example the order of two
 * amendments in a history line. A malformed input passes back through
 * unchanged, exactly as `formatDateGB` returns it.
 */
export function formatDateTimeGB(iso: string): string {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00.000Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * British month + year — `March 2026` (en-GB, `MMMM yyyy`). Accepts a full ISO
 * timestamp or a `YYYY-MM` / `YYYY-MM-DD` month key (anchored to UTC midnight so
 * it never drifts a month in negative timezones). Unparseable input passes back
 * through unchanged.
 */
export function formatMonthYearGB(iso: string): string {
    const normalised = iso.length === 7 ? `${iso}-01` : iso;
    const d = new Date(normalised.length <= 10 ? `${normalised}T00:00:00.000Z` : normalised);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Machine key → sentence case for display: `dateOfBirth` → "Date of birth",
 * `no_health_concerns` → "No health concerns", `addressLine1` → "Address line 1".
 *
 * This is the FALLBACK for keys with no curated label, not a substitute for one —
 * a hand-written label always reads better and survives a key rename. Reach for a
 * `Record<key, string>` map first (see `contact-labels.ts`) and let this catch
 * whatever the map does not name, so a new key degrades to readable prose rather
 * than leaking camelCase into the UI.
 */
export function humaniseKey(key: string): string {
    const spaced = key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    if (spaced.length === 0) return key;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
